// ** MUI Imports
import { CardMedia, Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { getCookie } from 'cookies-next'

// ** Icons Imports
import { Close } from 'mdi-material-ui'
import { useEffect, useMemo, useState } from 'react'
import UserApi from 'src/@core/utils/api/user-api'
import { randomNumber, sendEvent } from 'src/@core/utils/functions'
import { OfferTargetLead } from 'src/@core/utils/types'
import validator from 'validator'

type LeadCardProps = {
  timeout: number
}

const LeadCard = ({ timeout }: LeadCardProps) => {
  const sessionStart = useMemo(() => Date.now(), [])
  const [email, setEmail] = useState('');
  const [targetId, setTargetId] = useState('');
  const [emailError, setEmaiError] = useState(true);
  const [offerTargetLead, setOfferTargetLead] = useState<OfferTargetLead>();


  useEffect(() => {
    const timeoutHandler = setTimeout(async () => {
      let target = getCookie('_ga');
      if (!target) {
        target = `InK1.1.${randomNumber(1000000000, 9999999999)}.${Date.now()}`
      }
      setTargetId(target);
      setOfferTargetLead(await UserApi.getNextOffer(target));
    }, timeout);

    return () => clearTimeout(timeoutHandler)
  }, [timeout])

  useEffect(() => {
    if (!offerTargetLead) {
      return;
    }
    sendEvent('offer_presented', {
      'offer_id': offerTargetLead?.offer._id
    })
  }, [offerTargetLead])

  const handleClose = () => {
    sendEvent('offer_dismiss', {
      'offer_id': offerTargetLead?.offer._id,
      'engagement_time': Date.now() - sessionStart
    })
    setOfferTargetLead(undefined)
  };

  const checkEmail = () => {
    const valid = validator.isEmail(email);
    setEmaiError(!valid);

    return valid;
  }

  const sendOffer = async () => {
    if (!checkEmail()) {
      return;
    }

    // collect email
    try {
      sendEvent('create_lead', {
        'offer_id': offerTargetLead?.offer._id,
        'engagement_time': Date.now() - sessionStart
      })
      await UserApi.collectLead(email, targetId, offerTargetLead?.offer._id || '')
    } catch { }

    // open offer in a new tab
    setOfferTargetLead(undefined);
  }

  if (!offerTargetLead) {
    return null;
  }

  return (
    <Dialog open={true}>
      <DialogTitle id='alert-dialog-title' sx={{ color: 'red' }}>🚨 One Time Special Offer 😱 !!!</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <CardMedia sx={{ height: '14.5625rem', backgroundSize: 'contain' }} image={offerTargetLead?.offer.imagePath} />
        <Grid container>
          <Grid xs={12} pt={4} item>
            <Typography variant='h6' sx={{ marginBottom: 2.75 }}>
              {offerTargetLead?.offer.headline}
            </Typography>
            <Typography>
              {offerTargetLead?.offer.caption}
            </Typography>
          </Grid>
          <Grid xs={12} pt={4} item>
            <TextField label="Enter your email address to proceed" variant="standard"
              autoFocus fullWidth type={'email'} onChange={e => {
                setEmail(e.target.value)
                checkEmail();
              }}
              error={emailError} onBlur={checkEmail} />
          </Grid>
          <Grid xs={12} pt={4} item justifyContent={'center'} display={'flex'}>
            <Button variant='contained' onClick={sendOffer} disabled={emailError} href={offerTargetLead?.offer.offerURL} target='_blank'>Get Offer</Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default LeadCard
